/**
 * Frontend client for the Approvals API (server/modules/approvals). Authed calls
 * go through the shared apiClient (auth + workspace headers injected); the public
 * share-link calls use plain fetch (no auth) and read the {data} envelope.
 */
import { apiGet, apiPost, apiPatch } from "../shared/apiClient";

const API = "";

export interface Pin {
  x: number;
  y: number;
}

export type ApprovalStatus = "awaiting" | "changes" | "approved";

export interface Approval {
  _id: string;
  workspaceId: string;
  targetType: "file" | "collection";
  targetFileId: string | null;
  targetCollectionId: string | null;
  shareToken?: string;
  clientName?: string;
  status: ApprovalStatus;
  assigneeId: string | null;
  sentAt?: string;
  viewedAt?: string;
  approvedAt?: string;
  hadChangesRound: boolean;
  sentToProductionAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Comment {
  _id: string;
  workspaceId: string;
  targetFileId: string | null;
  approvalId: string | null;
  shareToken?: string;
  authorName?: string;
  authorUserId: string | null;
  body: string;
  pin: Pin | null;
  pinned: boolean;
  status: "open" | "resolved";
  createdAt: string;
  updatedAt: string;
}

// ─────────────────────────── Authed ─────────────────────────────────────────

export const listApprovals = () => apiGet<Approval[]>("/approvals");

export const createApproval = (input: {
  targetFileId: string;
  shareToken?: string;
  clientName?: string;
  assigneeId?: string | null;
}) => apiPost<Approval>("/approvals", input);

export const updateApproval = (
  id: string,
  patch: { status?: ApprovalStatus; assigneeId?: string | null; sentToProduction?: boolean },
) => apiPatch<Approval>(`/approvals/${id}`, patch);

export const listComments = (approvalId: string) =>
  apiGet<Comment[]>(`/approvals/${approvalId}/comments`);

export const addInternalComment = (approvalId: string, body: string, pin?: Pin) =>
  apiPost<Comment>(`/approvals/${approvalId}/comments`, { body, ...(pin ? { pin } : {}) });

// ─────────────────────────── Public (share link, no auth) ───────────────────

async function publicRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API}/api/v1${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || "Request failed");
  return json.data as T;
}

export const publicApprove = (
  token: string,
  input: { clientName?: string; decision: "approved" | "changes" },
) => publicRequest<Approval>("POST", `/approvals/${token}/approve`, input);

export const publicComment = (
  token: string,
  input: { authorName: string; body: string; pin?: Pin },
) => publicRequest<Comment>("POST", `/approvals/${token}/comments`, input);

export const publicListComments = (token: string) =>
  publicRequest<Comment[]>("GET", `/approvals/${token}/comments`);
